import { getSupabase } from './client';
import { getCachedMember } from './auth-session';
import { courseHcpForPlayer } from '@/lib/games/engine/helpers';
import { formatJoinError, formatDeleteRoundError } from './join-errors';
import { parsePresses, pressConfigUpdates } from '@/lib/games/engine/presses';
import type { MatchPress, RoundContext } from '@/lib/games/engine/types';
import type {
  GroupRound,
  GroupRoundEnriched,
  GroupRoundGame,
  GroupRoundJoinRequest,
  GroupRoundPlayer,
  GroupRoundScore,
  GroupRoundSettlement,
  GroupRoundStatus,
  GroupRoundListItem,
  HoleRange,
  ISODateString,
  RoundTemplate,
  ScoreChangeLog,
  SimplifiedPayment,
  UUID,
  Member,
} from './types';

async function requireMe(): Promise<Member> {
  const me = await getCachedMember();
  if (!me) throw new Error('Not authenticated');
  return me;
}

export function playerKeyFor(memberId?: UUID | null, guestName?: string | null): string {
  if (memberId) return `member:${memberId}`;
  if (guestName) return `guest:${guestName.trim().toLowerCase().replace(/\s+/g, '_')}`;
  throw new Error('player needs member_id or guest_name');
}

async function enrichPlayersWithMemberProfiles(players: GroupRoundPlayer[]): Promise<GroupRoundPlayer[]> {
  const memberIds = [...new Set(players.map((p) => p.member_id).filter(Boolean))] as UUID[];
  if (!memberIds.length) return players;

  const { data, error } = await getSupabase()
    .from('members')
    .select('id, avatar, profile_photo_url')
    .in('id', memberIds);
  if (error) throw error;

  const byId = new Map((data || []).map((m) => [m.id as string, m]));
  return players.map((p) => {
    if (!p.member_id) return p;
    const m = byId.get(p.member_id);
    if (!m) return p;
    return {
      ...p,
      avatar: (m.avatar as string | null) ?? null,
      profile_photo_url: (m.profile_photo_url as string | null) ?? null,
    };
  });
}

async function attachPlayersToRounds(rounds: GroupRound[]): Promise<GroupRoundListItem[]> {
  if (!rounds.length) return [];
  const sb = getSupabase();
  const ids = rounds.map((r) => r.id);
  const { data, error } = await sb
    .from('group_round_players')
    .select('*')
    .in('group_round_id', ids)
    .order('sort_order');
  if (error) throw error;

  const byRound = new Map<string, GroupRoundPlayer[]>();
  for (const row of data || []) {
    const p = row as GroupRoundPlayer;
    const list = byRound.get(p.group_round_id) || [];
    list.push(p);
    byRound.set(p.group_round_id, list);
  }

  return rounds.map((r) => ({
    ...r,
    players: byRound.get(r.id) || [],
  }));
}

async function fetchEnriched(id: UUID): Promise<GroupRoundEnriched | null> {
  const sb = getSupabase();
  const { data: round, error } = await sb.from('group_rounds').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!round) return null;

  const [players, games, scores, settlement] = await Promise.all([
    sb.from('group_round_players').select('*').eq('group_round_id', id).order('sort_order'),
    sb.from('group_round_games').select('*').eq('group_round_id', id).order('sort_order'),
    sb.from('group_round_scores').select('*').eq('group_round_id', id),
    sb.from('group_round_settlements').select('*').eq('group_round_id', id).maybeSingle(),
  ]);

  if (players.error) throw players.error;
  if (games.error) throw games.error;
  if (scores.error) throw scores.error;
  if (settlement.error) throw settlement.error;

  const playerRows = await enrichPlayersWithMemberProfiles((players.data || []) as GroupRoundPlayer[]);

  return {
    ...(round as GroupRound),
    players: playerRows,
    games: (games.data || []) as GroupRoundGame[],
    scores: (scores.data || []) as GroupRoundScore[],
    settlement: (settlement.data as GroupRoundSettlement | null) ?? null,
  };
}

export const groupRounds = {
  async create(input: {
    course_id: UUID;
    tee_id?: UUID;
    played_at?: ISODateString;
    holes?: HoleRange;
    template_id?: UUID;
  }): Promise<GroupRound> {
    const me = await requireMe();
    const { data, error } = await getSupabase()
      .from('group_rounds')
      .insert({
        organizer_id: me.id,
        course_id: input.course_id,
        tee_id: input.tee_id ?? null,
        played_at: input.played_at ?? new Date().toISOString(),
        holes: input.holes ?? 'full',
        status: 'setup',
        template_id: input.template_id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as GroupRound;
  },

  async get(id: UUID): Promise<GroupRoundEnriched | null> {
    return fetchEnriched(id);
  },

  async listActive(): Promise<GroupRound[]> {
    const me = await requireMe();
    const sb = getSupabase();
    const { data: hiddenRows, error: hiddenErr } = await sb
      .from('group_round_hidden_history')
      .select('group_round_id')
      .eq('member_id', me.id);
    if (hiddenErr) throw hiddenErr;
    const hiddenIds = new Set((hiddenRows || []).map((r) => String(r.group_round_id)));

    const { data: asOrg, error: e1 } = await sb
      .from('group_rounds')
      .select('*')
      .eq('status', 'active')
      .eq('organizer_id', me.id)
      .order('updated_at', { ascending: false });
    if (e1) throw e1;

    const { data: asPlayer, error: e2 } = await sb
      .from('group_round_players')
      .select('group_round_id')
      .eq('member_id', me.id);
    if (e2) throw e2;

    const playerRoundIds = (asPlayer || []).map((r) => r.group_round_id);
    let fromPlayer: GroupRound[] = [];
    if (playerRoundIds.length) {
      const { data, error } = await sb
        .from('group_rounds')
        .select('*')
        .in('id', playerRoundIds)
        .eq('status', 'active');
      if (error) throw error;
      fromPlayer = (data || []) as GroupRound[];
    }

    const merged = new Map<string, GroupRound>();
    [...(asOrg || []), ...fromPlayer].forEach((r) => merged.set(r.id, r));
    return [...merged.values()]
      .filter((r) => !hiddenIds.has(r.id))
      .sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  },

  async listActiveSummary(): Promise<GroupRoundListItem[]> {
    const rounds = await this.listActive();
    return attachPlayersToRounds(rounds);
  },

  async listHistory(limit = 30): Promise<GroupRound[]> {
    const me = await requireMe();
    const sb = getSupabase();
    const { data: hiddenRows, error: hiddenErr } = await sb
      .from('group_round_hidden_history')
      .select('group_round_id')
      .eq('member_id', me.id);
    if (hiddenErr) throw hiddenErr;
    const hiddenIds = new Set((hiddenRows || []).map((r) => String(r.group_round_id)));

    const { data: asOrg, error: e1 } = await sb
      .from('group_rounds')
      .select('*')
      .eq('organizer_id', me.id)
      .eq('status', 'completed')
      .order('played_at', { ascending: false })
      .limit(limit);
    if (e1) throw e1;

    const { data: asPlayer, error: e2 } = await sb
      .from('group_round_players')
      .select('group_round_id')
      .eq('member_id', me.id);
    if (e2) throw e2;

    const playerRoundIds = (asPlayer || []).map((r) => r.group_round_id);
    let fromPlayer: GroupRound[] = [];
    if (playerRoundIds.length) {
      const { data, error } = await sb
        .from('group_rounds')
        .select('*')
        .in('id', playerRoundIds)
        .eq('status', 'completed')
        .order('played_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      fromPlayer = (data || []) as GroupRound[];
    }

    const merged = new Map<string, GroupRound>();
    [...(asOrg || []), ...fromPlayer].forEach((r) => merged.set(r.id, r));
    return [...merged.values()]
      .filter((r) => !hiddenIds.has(r.id))
      .sort((a, b) => new Date(b.played_at).getTime() - new Date(a.played_at).getTime())
      .slice(0, limit);
  },

  async listHistoryEnriched(limit = 30): Promise<GroupRoundEnriched[]> {
    const rounds = await this.listHistory(limit);
    const enriched: GroupRoundEnriched[] = [];
    for (const r of rounds) {
      const full = await fetchEnriched(r.id);
      if (full) enriched.push(full);
    }
    return enriched;
  },

  async listHistorySummary(limit = 30): Promise<GroupRoundListItem[]> {
    const rounds = await this.listHistory(limit);
    return attachPlayersToRounds(rounds);
  },

  async listJoinable(): Promise<GroupRoundEnriched[]> {
    const me = await requireMe();
    const sb = getSupabase();
    const { data, error } = await sb
      .from('group_rounds')
      .select('*')
      .eq('status', 'active')
      .eq('config->>is_joinable', 'true')
      .order('updated_at', { ascending: false });
    if (error) throw error;

    const rounds = (data || []) as GroupRound[];
    const enriched: GroupRoundEnriched[] = [];
    for (const r of rounds) {
      if (r.organizer_id === me.id) continue;
      const full = await fetchEnriched(r.id);
      if (!full) continue;
      if ((full.players?.length ?? 0) >= 4) continue;
      if (full.players?.some((p) => p.member_id === me.id)) continue;
      enriched.push(full);
    }
    return enriched;
  },

  async listJoinableSummary(): Promise<GroupRoundListItem[]> {
    const me = await requireMe();
    const sb = getSupabase();
    const { data, error } = await sb
      .from('group_rounds')
      .select('*')
      .eq('status', 'active')
      .eq('config->>is_joinable', 'true')
      .order('updated_at', { ascending: false });
    if (error) throw error;

    const rounds = (data || []) as GroupRound[];
    const candidates = rounds.filter(
      (r) => r.organizer_id !== me.id && Boolean(r.config?.is_joinable),
    );
    if (!candidates.length) return [];

    const withPlayers = await attachPlayersToRounds(candidates);
    return withPlayers.filter((full) => {
      if ((full.players?.length ?? 0) >= 4) return false;
      if (full.players?.some((p) => p.member_id === me.id)) return false;
      return true;
    });
  },

  async join(roundId: UUID): Promise<GroupRoundPlayer> {
    const me = await requireMe();
    const sb = getSupabase();

    const { data: rpcData, error: rpcError } = await sb.rpc('join_group_round', { p_round_id: roundId });
    if (!rpcError && rpcData) {
      return rpcData as GroupRoundPlayer;
    }

    const round = await fetchEnriched(roundId);
    if (!round) throw new Error('Round not found');
    if (round.status !== 'active') throw new Error('Round is not active');
    if (!round.config?.is_joinable) throw new Error('This round is not open to join');
    if ((round.players?.length ?? 0) >= 4) throw new Error('Group is full');
    if (round.players?.some((p) => p.member_id === me.id)) throw new Error('You are already in this group');

    let slope = 130;
    if (round.course_id && round.tee_id) {
      const { data: tee } = await getSupabase()
        .from('course_tees')
        .select('slope')
        .eq('id', round.tee_id)
        .maybeSingle();
      slope = tee?.slope ?? 130;
    }

    const sortOrder = round.players?.length ?? 0;
    const { data, error } = await sb
      .from('group_round_players')
      .insert({
        group_round_id: roundId,
        member_id: me.id,
        guest_name: null,
        player_key: playerKeyFor(me.id),
        display_name: me.name,
        handicap: me.handicap ?? null,
        course_handicap: courseHcpForPlayer(me.handicap, slope),
        tee_id: round.tee_id,
        team_index: null,
        sort_order: sortOrder,
        is_organizer: false,
      })
      .select()
      .single();
    if (error) {
      const msg = formatJoinError(rpcError?.message, error.message);
      throw new Error(msg);
    }
    return data as GroupRoundPlayer;
  },

  /** Ask the organizer of an open round for permission to join. */
  async requestJoin(roundId: UUID): Promise<GroupRoundJoinRequest> {
    const sb = getSupabase();
    const { data, error } = await sb.rpc('request_group_round_join', { p_round_id: roundId });
    if (error) throw new Error(formatJoinError(error.message, error.message));
    return data as GroupRoundJoinRequest;
  },

  /** Round ids the current member has an outstanding (pending) join request for. */
  async listMyPendingRequestRoundIds(): Promise<UUID[]> {
    const me = await requireMe();
    const sb = getSupabase();
    const { data, error } = await sb
      .from('group_round_join_requests')
      .select('group_round_id')
      .eq('member_id', me.id)
      .eq('status', 'pending');
    if (error) throw error;
    return (data || []).map((r) => r.group_round_id as UUID);
  },

  /** Pending join requests for a round the current member organizes. */
  async listJoinRequests(roundId: UUID): Promise<GroupRoundJoinRequest[]> {
    const sb = getSupabase();
    const { data, error } = await sb
      .from('group_round_join_requests')
      .select('*')
      .eq('group_round_id', roundId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true });
    if (error) throw error;

    const rows = (data || []) as GroupRoundJoinRequest[];
    if (!rows.length) return rows;

    const memberIds = [...new Set(rows.map((r) => r.member_id))];
    const { data: members } = await sb
      .from('members')
      .select('id, name, avatar, profile_photo_url')
      .in('id', memberIds);
    const byId = new Map((members || []).map((m) => [m.id as string, m]));
    return rows.map((r) => {
      const m = byId.get(r.member_id);
      return {
        ...r,
        requester_name: (m?.name as string | undefined) ?? 'Player',
        requester_avatar: (m?.avatar as string | null) ?? null,
        requester_photo_url: (m?.profile_photo_url as string | null) ?? null,
      };
    });
  },

  /** Organizer accepts or declines a pending join request. */
  async respondJoin(requestId: UUID, accept: boolean): Promise<GroupRoundJoinRequest> {
    const sb = getSupabase();
    const { data, error } = await sb.rpc('respond_group_round_join', {
      p_request_id: requestId,
      p_accept: accept,
    });
    if (error) throw new Error(formatJoinError(error.message, error.message));
    return data as GroupRoundJoinRequest;
  },

  async transferOrganizer(roundId: UUID, newMemberId: UUID): Promise<void> {
    const round = await fetchEnriched(roundId);
    if (!round?.players?.length) throw new Error('Round not found');
    if (!round.players.some((p) => p.member_id === newMemberId)) {
      throw new Error('New organizer must be a player in this round');
    }

    const sb = getSupabase();
    const { error: roundErr } = await sb
      .from('group_rounds')
      .update({ organizer_id: newMemberId })
      .eq('id', roundId);
    if (roundErr) throw roundErr;

    const updated = round.players.map((p) => ({
      member_id: p.member_id ?? undefined,
      guest_name: p.guest_name ?? undefined,
      display_name: p.display_name,
      handicap: p.handicap,
      course_handicap: p.course_handicap,
      tee_id: p.tee_id ?? undefined,
      team_index: p.team_index,
      is_organizer: p.member_id === newMemberId,
    }));
    await this.setPlayers(roundId, updated);
  },

  async cancel(roundId: UUID): Promise<GroupRound> {
    return this.update(roundId, { status: 'cancelled' as GroupRoundStatus });
  },

  async delete(roundId: UUID): Promise<void> {
    const me = await requireMe();
    const round = await fetchEnriched(roundId);
    if (!round) throw new Error('Round not found');
    if (round.organizer_id !== me.id) throw new Error('Only the organizer can delete this round');
    const { error } = await getSupabase()
      .from('group_rounds')
      .delete()
      .eq('id', roundId);
    if (error) throw new Error(formatDeleteRoundError(error.message));
  },

  async hideFromMyHistory(roundId: UUID): Promise<void> {
    const me = await requireMe();
    const sb = getSupabase();
    const round = await fetchEnriched(roundId);
    if (!round) throw new Error('Round not found');
    const inRound = round.organizer_id === me.id || (round.players || []).some((p) => p.member_id === me.id);
    if (!inRound) throw new Error('You can only hide rounds you played in');
    const { error } = await sb
      .from('group_round_hidden_history')
      .upsert({ member_id: me.id, group_round_id: roundId }, { onConflict: 'member_id,group_round_id' });
    if (error) throw error;
  },

  subscribeScores(roundId: UUID, cb: () => void): () => void {
    const ch = getSupabase()
      .channel(`group-round-scores-${roundId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_round_scores', filter: `group_round_id=eq.${roundId}` },
        () => cb(),
      )
      .subscribe();
    return () => { getSupabase().removeChannel(ch); };
  },

  async update(id: UUID, patch: Partial<Pick<GroupRound, 'course_id' | 'tee_id' | 'played_at' | 'holes' | 'status' | 'config'>>): Promise<GroupRound> {
    const { data, error } = await getSupabase()
      .from('group_rounds')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as GroupRound;
  },

  async setPlayers(
    roundId: UUID,
    players: Array<{
      member_id?: UUID;
      guest_name?: string;
      display_name: string;
      handicap?: number | null;
      course_handicap?: number;
      tee_id?: UUID;
      team_index?: number | null;
      is_organizer?: boolean;
    }>,
  ): Promise<GroupRoundPlayer[]> {
    const me = await requireMe();
    const sb = getSupabase();
    await sb.from('group_round_players').delete().eq('group_round_id', roundId);

    const rows = players.map((p, i) => ({
      group_round_id: roundId,
      member_id: p.member_id ?? null,
      guest_name: p.guest_name ?? null,
      player_key: playerKeyFor(p.member_id, p.guest_name),
      display_name: p.display_name,
      handicap: p.handicap ?? null,
      course_handicap: p.course_handicap ?? 0,
      tee_id: p.tee_id ?? null,
      team_index: p.team_index ?? null,
      sort_order: i,
      is_organizer: p.is_organizer ?? false,
    }));

    if (!rows.length) return [];
    const { data, error } = await sb.from('group_round_players').insert(rows).select();
    if (error) throw error;
    return (data || []) as GroupRoundPlayer[];
  },

  async setGames(
    roundId: UUID,
    games: Array<{ format_id: string; enabled?: boolean; stakes?: Record<string, unknown>; config?: Record<string, unknown> }>,
  ): Promise<GroupRoundGame[]> {
    const sb = getSupabase();
    await sb.from('group_round_games').delete().eq('group_round_id', roundId);
    if (!games.length) return [];
    const rows = games.map((g, i) => ({
      group_round_id: roundId,
      format_id: g.format_id,
      enabled: g.enabled !== false,
      stakes: g.stakes ?? {},
      config: g.config ?? {},
      sort_order: i,
    }));
    const { data, error } = await sb.from('group_round_games').insert(rows).select();
    if (error) throw error;
    return (data || []) as GroupRoundGame[];
  },

  async updateGameConfig(
    roundId: UUID,
    formatId: string,
    config: Record<string, unknown>,
  ): Promise<void> {
    const sb = getSupabase();
    const { data: row } = await sb
      .from('group_round_games')
      .select('id, config')
      .eq('group_round_id', roundId)
      .eq('format_id', formatId)
      .maybeSingle();
    if (!row) return;
    const { error } = await sb
      .from('group_round_games')
      .update({ config })
      .eq('id', row.id);
    if (error) throw error;
  },

  async appendPress(roundId: UUID, formatId: string, press: MatchPress): Promise<void> {
    const sb = getSupabase();
    const { data: row } = await sb
      .from('group_round_games')
      .select('id, config')
      .eq('group_round_id', roundId)
      .eq('format_id', formatId)
      .maybeSingle();
    if (!row) throw new Error('Game not found on this round');
    const existing = parsePresses((row.config || {}) as Record<string, unknown>);
    const config = { ...(row.config as Record<string, unknown>), presses: [...existing, press] };
    const { error } = await sb.from('group_round_games').update({ config }).eq('id', row.id);
    if (error) throw error;
  },

  async syncPressStatuses(roundId: UUID, games: GroupRoundGame[], ctx: RoundContext): Promise<boolean> {
    const updates = pressConfigUpdates(games, ctx);
    if (!updates.length) return false;
    await Promise.all(
      updates.map((u) => this.updateGameConfig(roundId, u.formatId, u.config)),
    );
    return true;
  },

  async start(roundId: UUID): Promise<GroupRound> {
    return this.update(roundId, { status: 'active' as GroupRoundStatus });
  },

  async upsertScore(
    roundId: UUID,
    playerKey: string,
    holeNum: number,
    strokes: number | null,
    putts?: number | null,
  ): Promise<void> {
    const me = await requireMe();
    const sb = getSupabase();

    const { data: existing } = await sb
      .from('group_round_scores')
      .select('strokes, putts')
      .eq('group_round_id', roundId)
      .eq('player_key', playerKey)
      .eq('hole_num', holeNum)
      .maybeSingle();

    const oldStrokes = existing?.strokes ?? null;
    const oldPutts = existing?.putts ?? null;
    const newPutts = putts !== undefined ? putts : oldPutts;
    if (oldStrokes === strokes && oldPutts === newPutts) return;

    const { error: upsertErr } = await sb.from('group_round_scores').upsert({
      group_round_id: roundId,
      player_key: playerKey,
      hole_num: holeNum,
      strokes,
      putts: newPutts,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'group_round_id,player_key,hole_num' });
    if (upsertErr) throw upsertErr;

    if (oldStrokes !== strokes) {
      const { error: logErr } = await sb.from('score_change_log').insert({
        group_round_id: roundId,
        player_key: playerKey,
        hole_num: holeNum,
        old_strokes: oldStrokes,
        new_strokes: strokes,
        actor_id: me.id,
      });
      if (logErr) throw logErr;
    }
  },

  async getScoreLog(roundId: UUID): Promise<ScoreChangeLog[]> {
    const { data, error } = await getSupabase()
      .from('score_change_log')
      .select('*')
      .eq('group_round_id', roundId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as ScoreChangeLog[];
  },

  async complete(
    roundId: UUID,
    settlement: {
      net_by_player: Record<string, number>;
      simplified_payments: SimplifiedPayment[];
      game_results: Record<string, unknown>[];
    },
  ): Promise<GroupRoundSettlement> {
    const sb = getSupabase();
    await sb.from('group_round_settlements').delete().eq('group_round_id', roundId);
    const { data, error } = await sb
      .from('group_round_settlements')
      .insert({
        group_round_id: roundId,
        net_by_player: settlement.net_by_player,
        simplified_payments: settlement.simplified_payments,
        game_results: settlement.game_results,
      })
      .select()
      .single();
    if (error) throw error;
    await this.update(roundId, { status: 'completed' });
    return data as GroupRoundSettlement;
  },

  async setLegacyLinks(roundId: UUID, legacyRoundId?: UUID, legacyPostId?: UUID): Promise<void> {
    const patch: Partial<GroupRound> = {};
    if (legacyRoundId) patch.legacy_round_id = legacyRoundId;
    if (legacyPostId) patch.legacy_post_id = legacyPostId;
    if (Object.keys(patch).length) await this.update(roundId, patch);
  },

  /** Resolve a completed group round from its feed recap post (for reopening settlement). */
  async findIdByLegacyPostId(postId: UUID): Promise<UUID | null> {
    const { data, error } = await getSupabase()
      .from('group_rounds')
      .select('id')
      .eq('legacy_post_id', postId)
      .maybeSingle();
    if (error) throw error;
    return data?.id ?? null;
  },

  templates: {
    async list(): Promise<RoundTemplate[]> {
      const me = await requireMe();
      const { data, error } = await getSupabase()
        .from('round_templates')
        .select('*')
        .eq('owner_id', me.id)
        .order('last_used_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data || []) as RoundTemplate[];
    },

    async getLast(): Promise<RoundTemplate | null> {
      const list = await this.list();
      return list[0] ?? null;
    },

    async save(input: Omit<RoundTemplate, 'id' | 'owner_id' | 'created_at' | 'updated_at' | 'last_used_at'> & { id?: UUID }): Promise<RoundTemplate> {
      const me = await requireMe();
      const sb = getSupabase();
      if (input.id) {
        const { data, error } = await sb
          .from('round_templates')
          .update({ ...input, last_used_at: new Date().toISOString() })
          .eq('id', input.id)
          .eq('owner_id', me.id)
          .select()
          .single();
        if (error) throw error;
        return data as RoundTemplate;
      }
      const { data, error } = await sb
        .from('round_templates')
        .insert({ ...input, owner_id: me.id, last_used_at: new Date().toISOString() })
        .select()
        .single();
      if (error) throw error;
      return data as RoundTemplate;
    },

    async touch(id: UUID): Promise<void> {
      const { error } = await getSupabase()
        .from('round_templates')
        .update({ last_used_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
  },
};
